import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "react-query";
import { Toaster } from "react-hot-toast";
import { HelmetProvider } from "react-helmet-async";

// Contexts
import { AuthProvider, useAuth } from "./contexts/AuthContext";

// Components
import Layout from "./components/Layout/Layout";
import LoadingSpinner from "./components/Common/LoadingSpinner";

// Pages
import Login from "./pages/Auth/Login";
import Dashboard from "./pages/Dashboard/Dashboard";
import Users from "./pages/Users/Users";
import Bookings from "./pages/Bookings/Bookings";
import Movies from "./pages/Movies/Movies";
import Cinemas from "./pages/Cinemas/Cinemas";
import Pages from "./pages/CMS/Pages";
import Banners from "./pages/CMS/Banners";
import MamangamHallBooking from "./pages/CMS/MamangamHallBooking";
import AdvertiseWithUs from "./pages/CMS/AdvertiseWithUs";
import AboutUs from "./pages/CMS/AboutUs";
import SpecialScreeningBulkBooking from "./pages/CMS/SpecialScreeningBulkBooking";

// import Media from './pages/CMS/Media'; // Media tab hidden
import Notifications from "./pages/Notifications/Notifications";
import Templates from "./pages/Notifications/Templates";
import NotificationCenter from "./pages/Notifications/NotificationCenter";
import Analytics from "./pages/Analytics/Analytics";
import BookingsReport from "./pages/Reports/BookingsReport";
import CancellationsReport from "./pages/Reports/CancellationsReport";
import ManualRefundsReport from "./pages/Reports/ManualRefundsReport";
import PayoutSummaryReport from "./pages/Reports/PayoutSummaryReport";
import Settings from "./pages/Settings/Settings";
import BookingFeeTiers from "./pages/Settings/BookingFeeTiers";
import AdminUsers from "./pages/AdminUsers/AdminUsers";
import Coupons from "./pages/Coupons/Coupons";
import Beverages from "./pages/Beverages/Beverages";
import Offers from "./pages/Offers/Offers";
import Cities from "./pages/Cities/Cities";

// Styles
import "./assets/css/App.css";
import ContactReportsPage from "./pages/Contact/ContactReport";

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Public Route Component (redirect to dashboard if authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <Router>
            <div className="App">
              <Routes>
                {/* Public Routes */}
                <Route
                  path="/login"
                  element={
                    <PublicRoute>
                      <Login />
                    </PublicRoute>
                  }
                />

                {/* Protected Routes */}
                <Route
                  path="/*"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Routes>
                          <Route
                            path="/"
                            element={<Navigate to="/dashboard" replace />}
                          />
                          <Route path="/dashboard" element={<Dashboard />} />
                          <Route path="/users" element={<Users />} />
                          <Route path="/bookings" element={<Bookings />} />
                          <Route path="/movies" element={<Movies />} />
                          <Route path="/cinemas" element={<Cinemas />} />
                          <Route path="/cms/pages" element={<Pages />} />
                          <Route path="/cms/banners" element={<Banners />} />
                          <Route
                            path="/cms/mamangam-hall-booking"
                            element={<MamangamHallBooking />}
                          />
                          <Route
                            path="/cms/advertise-with-us"
                            element={<AdvertiseWithUs />}
                          />
                          <Route path="/cms/about-us" element={<AboutUs />} />
                          <Route
                            path="/cms/special-screening-bulk-booking"
                            element={<SpecialScreeningBulkBooking />}
                          />
                          {/* <Route path="/cms/media" element={<Media />} /> Media tab hidden */}
                          <Route
                            path="/notifications"
                            element={<NotificationCenter />}
                          />
                          <Route
                            path="/notification-center"
                            element={<NotificationCenter />}
                          />
                          <Route
                            path="/brevo-sync"
                            element={<Notifications />}
                          />
                          <Route
                            path="/notifications/templates"
                            element={<Templates />}
                          />
                          <Route path="/analytics" element={<Analytics />} />
                          <Route
                            path="/reports/bookings"
                            element={<BookingsReport />}
                          />
                          <Route
                            path="/reports/cancellations"
                            element={<CancellationsReport />}
                          />
                          <Route
                            path="/reports/manual-refunds"
                            element={<ManualRefundsReport />}
                          />
                          <Route
                            path="/reports/payout-summary"
                            element={<PayoutSummaryReport />}
                          />
                          <Route path="/settings" element={<Settings />} />
                          <Route
                            path="/settings/booking-fee-tiers"
                            element={<BookingFeeTiers />}
                          />
                          <Route path="/admin-users" element={<AdminUsers />} />
                          <Route path="/coupons" element={<Coupons />} />
                          <Route path="/beverages" element={<Beverages />} />
                          <Route path="/offers" element={<Offers />} />
                          <Route path="/cities" element={<Cities />} />

                          <Route
                            path="/contact"
                            element={<ContactReportsPage />}
                          />
                          <Route
                            path="*"
                            element={<Navigate to="/dashboard" replace />}
                          />
                        </Routes>
                      </Layout>
                    </ProtectedRoute>
                  }
                />
              </Routes>

              {/* Toast Notifications */}
              <Toaster
                position="top-right"
                toastOptions={{
                  duration: 4000,
                  style: {
                    background: "#363636",
                    color: "#fff",
                  },
                  success: {
                    duration: 3000,
                    iconTheme: {
                      primary: "#4aed88",
                      secondary: "#fff",
                    },
                  },
                  error: {
                    duration: 5000,
                    iconTheme: {
                      primary: "#ff6b6b",
                      secondary: "#fff",
                    },
                  },
                }}
              />
            </div>
          </Router>
        </AuthProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
